import { Location } from '@angular/common';
import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacySlideToggleChange as MatSlideToggleChange } from '@angular/material/legacy-slide-toggle';
import { ActivatedRoute, Router } from '@angular/router';
import { DialogDeleteGenericComponent } from '@app/shared/components/dialog-delete-generic/dialog-delete-generic.component';
import { NotificationType } from '@app/shared/interfaces';
import { TitleService } from '@app/shared/services/title/title.service';
import { ToastNotificationsService } from '@app/shared/services/toast-notifications/toast-notifications.service';
import { timer } from 'rxjs';
import {
  ProductCatalogService,
  ProductItem,
  ProductVariation,
  SmartUpsellRule,
} from '../../services/product-catalog.service';

// Extend ProductItem interface for local use
interface ProductItemWithTiming extends ProductItem {
  timing?: {
    unit: {
      value: number;
      value_append: string;
      value_label?: string;
    };
  };
}

@Component({
  standalone: false,
  selector: 'sbnb-product-detail',
  templateUrl: './product-detail.component.html',
  styleUrls: ['./product-detail.component.scss'],
})
export class ProductDetailComponent implements OnInit {
  @ViewChild('nameSpan') nameSpan: ElementRef;
  @ViewChild('publicNameSpan') publicNameSpan: ElementRef;

  // Update the type to use our extended interface
  product: ProductItemWithTiming;
  loading = true;
  saveStatus = 0;
  editingPublicName = false;
  saveTimer: any;
  timingsMeta: any;
  smartUpsellRules: SmartUpsellRule[] = [];
  messagingRulesCollapsed = false;

  constructor(
    private location: Location,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private productCatalogService: ProductCatalogService,
    private toast: ToastNotificationsService,
    private titleService: TitleService,
    private router: Router
  ) {}

  ngOnInit() {
    this.titleService.manuallySet('Loading...');

    const productId = this.route.snapshot.paramMap.get('id');
    this.loadProduct(productId);
    this.setupTimingsMeta();
    this.loadSmartUpsellRules();
  }

  setupTimingsMeta() {
    this.timingsMeta = {
      modes: [
        {
          key: 'active',
          value: 'active',
          label: 'Active',
          overrideUnits: false,
        },
      ],
      units: [
        {
          type: 'int',
          text_after: 'hours',
          value_append: 'h',
          validation: [
            { type: 'min', value: 0 },
            { type: 'max', value: 72 },
          ],
        },
        {
          type: 'int',
          text_after: 'days',
          value_append: 'd',
          validation: [
            { type: 'min', value: 0 },
            { type: 'max', value: 30 },
          ],
        },
      ],
    };
  }

  timingUpdated() {
    // Convert the rule timing format back to our product format
    if (this.product.timing?.unit?.value_append === 'h') {
      this.product.timing_buffer = {
        value: this.product.timing.unit.value,
        unit: 'hours',
      };
    } else if (this.product.timing?.unit?.value_append === 'd') {
      this.product.timing_buffer = {
        value: this.product.timing.unit.value,
        unit: 'days',
      };
    }

    this.updateProduct();
  }

  loadProduct(id: string): void {
    this.productCatalogService.getProduct(id).subscribe(
      (data) => {
        this.product = data.data;

        // Convert timing_buffer to rule timing format
        if (this.product.timing_buffer) {
          this.product.timing = {
            unit: {
              value: this.product.timing_buffer.value,
              value_append: this.product.timing_buffer.unit === 'hours' ? 'h' : 'd',
            },
          };
        }

        this.loading = false;

        this.titleService.manuallySet(this.product.private_name);

        const isEarlyCheckinOrLateCheckout = ['early_checkin', 'late_checkout'].includes(this.product.type);

        if (isEarlyCheckinOrLateCheckout) {
          if (!this.product.timing_buffer) {
            this.product.timing_buffer = {
              value: 24,
              unit: 'hours',
            };
          }

          if (isEarlyCheckinOrLateCheckout && typeof this.product.blocks_day === 'undefined') {
            this.product.blocks_day = true;
          }
        }
      },
      (error) => {
        console.error('Error loading product:', error);
        this.loading = false;
        this.router.navigate(['/gx/upsells']);
      }
    );
  }

  updateProduct() {
    this.saveTimer?.unsubscribe();

    this.saveStatus = 1;
    this.productCatalogService.updateProduct(this.product).subscribe(
      () => {

        this.saveStatus = 2;

        this.saveTimer = timer(2000).subscribe(() => {
          this.saveStatus = 0;
        });
      },
      (error) => {
        this.saveStatus = -1;
        // console.error('Error updating product:', error);
        // this.toast.open('Error updating product', 'Dismiss', NotificationType.Error);
      }
    );
  }

  deleteProductPrompt() {
    const dialogRef = this.dialog.open(DialogDeleteGenericComponent, {
      width: '720px',
      data: {
        name: 'this product',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.deleteProduct();
      }
    });
  }

  deleteProduct() {
    this.productCatalogService.deleteProduct(this.product.id).subscribe((res) => {
      this.location.back();
    });
  }

  updateProductVariation(variation: ProductVariation) {
    this.saveTimer?.unsubscribe();

    this.saveStatus = 1;
    this.productCatalogService
      .updateProductVariation(this.product.id, variation.id, {
        price: variation.price.amount,
      })
      .subscribe(
        () => {
          this.saveStatus = 2;

          this.saveTimer = timer(2000).subscribe(() => {
            this.saveStatus = 0;
          });
        },
        (error) => {
          this.saveStatus = -1;
          console.error('Error updating product:', error);
          this.toast.open('Error updating product variation', 'Dismiss', NotificationType.Error);
        }
      );
  }

  addVariation() {
    alert('Coming soon');
  }

  removeVariation(index: number): void {
    alert('Coming soon');
  }

  handleScopeChange(scope: any) {
    this.product.all_platforms = scope.all_platforms;
    this.product.all_hosts = scope.all_hosts;
    this.product.all_properties = scope.all_properties;
    this.product.properties = scope.properties;
    this.product.hosts = scope.hosts;
    this.product.platforms = scope.platforms;

    this.updateProduct();
  }

  updateVariation(variation: ProductVariation, newPrice: string) {
    // Ensure the price is properly formatted as a Money object
    variation.price = {
      ...variation.price,
      amount: parseFloat(newPrice),
      formatted_decimal: variation.price.amount.toFixed(2),
      formatted_string: `${variation.price.currency}${variation.price.amount.toFixed(2)}`,
    };

    this.updateProductVariation(variation);
  }

  toggleVariationStatus(variation: ProductVariation, event: MatSlideToggleChange) {
    const newStatus = event.checked;
    const method = newStatus
      ? this.productCatalogService.activateProductVariation
      : this.productCatalogService.deactivateProductVariation;

    method.call(this.productCatalogService, this.product.id, variation.id).subscribe(
      () => {
        variation.active = newStatus;
      },
      (error) => {
        console.error('Error updating variation status:', error);
        this.toast.open('Error updating variation status', 'Dismiss', NotificationType.Error);
        // Revert the toggle if the API call fails
        event.source.checked = !newStatus;
      }
    );
  }

  toggleProductStatus(event: MatSlideToggleChange) {
    const newStatus = event.checked;
    this.productCatalogService.activeOrDeactivateProduct(this.product.id, newStatus).subscribe(
      (res: any) => {
        if (res.status === 422 && res.error?.message) {
          event.source.checked = !newStatus;
        } else {
          this.product.active = newStatus;
        }
      },
      (error) => {
        console.error('Error updating product status:', error);
        this.toast.open('Error updating product status', 'Dismiss', NotificationType.Error);

        // Revert the toggle if the API call fails
        event.source.checked = !newStatus;
      }
    );
  }

  loadSmartUpsellRules() {
    this.productCatalogService.getSmartUpsellRules().subscribe(
      (response) => {
        this.smartUpsellRules = response;
        // Set messagingRulesCollapsed to true if there's at least one rule
        this.messagingRulesCollapsed = this.smartUpsellRules.length > 0;
        console.log(this.smartUpsellRules);
      },
      (error) => {
        console.error('Error loading smart upsell rules:', error);
        this.smartUpsellRules = []; // Ensure it's an array even on error
        this.messagingRulesCollapsed = false;
      }
    );
  }

  openRule(ruleId: string) {
    this.router.navigate(['/gx/rule', ruleId]);
  }

  toggleMessagingRules() {
    this.messagingRulesCollapsed = !this.messagingRulesCollapsed;
  }
}
